// Ekran 05 — Üniteler API istemcisi.

import { api } from '../../lib/api'

export type UniteGrubu = 'on' | 'cur' | 'depo' | 'gaz' | 'enerji'
export type UniteDurumu = 'aktif' | 'bakimda' | 'pasif'
export type ParametreKaynagi = 'manuel' | 'lab' | 'olay'

/** K23 sıklık seti — sunucudaki Siklik.Saatlik ile aynı küme. */
export const SIKLIKLAR = ['1', '2', '3', '4', '6', '8', '12', '24'] as const

export const GRUP_ETIKET: Record<UniteGrubu, string> = {
  on: 'Ön İşlem',
  cur: 'Çürütme',
  depo: 'Depo',
  gaz: 'Gaz',
  enerji: 'Enerji',
}

export const FREK_ETIKET: Record<string, string> = {
  '1': 'Saatlik',
  '2': '2 saatte',
  '3': '3 saatte',
  '4': '4 saatte',
  '6': '6 saatte',
  '8': '8 saatte',
  '12': '12 saatte',
  // Referans :409 `FREK` sözlüğüyle birebir. Sunucudaki Siklik.Etiket() ayrı
  // bir sözlük (ekran 06'yı besler) — bilerek eşitlenmedi.
  '24': 'Günlük',
  olay: 'Olay bazlı',
  lab: 'Lab · dönemsiz',
}

export type SablonAlani = { anahtar: string; etiket: string; birim: string | null }
export type SablonParametresi = {
  ad: string
  birim: string | null
  frek: string
  kaynak: ParametreKaynagi
}

export type UniteSinifiDto = {
  id: string
  kodPrefix: string
  ad: string
  grup: UniteGrubu
  aciklama: string | null
  teknikAlanlar: SablonAlani[]
  parametreler: SablonParametresi[]
}

export type BeyanParametresiDto = {
  id: string
  uniteId: string
  ad: string
  birim: string | null
  frek: string
  kaynak: ParametreKaynagi
  sira: number
  /** K23: bu sıklıkta günde kaç hücre açılıyor. */
  gunlukAdet: number
  frekEtiket: string
}

export type UniteOzet = {
  id: string
  kod: string
  ad: string
  durum: UniteDurumu
  uniteSinifiId: string
  sinifKod: string
  sinifAd: string
  grup: UniteGrubu
  /**
   * Serbest METİN: "3000" da gelir, "Mezofilik 38–40°C" de (referans :468/476).
   * Sayı gerektiren yerlerde `sayiyaCevirBos` ile çözülür.
   */
  teknikDegerler: Record<string, string | null>
  parametreSayisi: number
  gunlukHucre: number
  /** Referans `sayac()` (ref:486-487) kırılımı — "Sıklık Dağılımı" sütunu. */
  saatlik: number
  periyodik: number
  olay: number
  lab: number
}

/**
 * Referans `frekOzet()` (ref:488-490): SABİT sıra — Saatlik · Periyodik · Olay
 * · Lab; sıfır olan parça hiç yazılmaz. Liste hücresi de tam sayfa kstrip'i de
 * aynı metni üretsin diye tek yerde duruyor.
 */
export function frekOzeti(k: {
  saatlik: number
  periyodik: number
  olay: number
  lab: number
}): string {
  const p: string[] = []
  if (k.saatlik) p.push(`Saatlik ${k.saatlik}`)
  if (k.periyodik) p.push(`Periyodik ${k.periyodik}`)
  if (k.olay) p.push(`Olay ${k.olay}`)
  if (k.lab) p.push(`Lab ${k.lab}`)
  return p.join(' · ') || '—'
}

/** Parametre listesinden aynı dört kovayı çıkarır (sunucu kırılımının eşi). */
export function frekKirilimi(parametreler: { frek: string }[]) {
  const saatlik = parametreler.filter((p) => p.frek === '1').length
  const olay = parametreler.filter((p) => p.frek === 'olay').length
  const lab = parametreler.filter((p) => p.frek === 'lab').length
  return { saatlik, periyodik: parametreler.length - saatlik - olay - lab, olay, lab }
}

export type UniteDetay = { unite: UniteOzet; parametreler: BeyanParametresiDto[] }

export type UniteOzeti = {
  toplam: number
  aktif: number
  bakimda: number
  pasif: number
  toplamParametre: number
  gunlukHucre: number
  toplamCurutucuHacmi: number | null
  kuruluGuc: number | null
  saatlikParametre: number
}

export type UniteIstek = {
  kod: string
  ad: string
  uniteSinifiId: string
  durum: UniteDurumu
  /** Okuma tarafıyla aynı sözleşme — ham metin gider, ham metin döner. */
  teknikDegerler?: Record<string, string | null> | null
}

export type ParametreIstek = {
  id: string | null
  ad: string
  birim: string | null
  frek: string
  kaynak: ParametreKaynagi
  sira: number
}

export const uniteler = {
  liste: (grup?: UniteGrubu) =>
    api<UniteOzet[]>(`/api/uniteler${grup ? `?grup=${grup}` : ''}`),

  ozet: () => api<UniteOzeti>('/api/uniteler/ozet'),

  siniflar: () => api<UniteSinifiDto[]>('/api/uniteler/siniflar'),

  detay: (id: string) => api<UniteDetay>(`/api/uniteler/${id}`),

  sonrakiKod: (sinifId: string) =>
    api<{ kod: string }>(`/api/uniteler/sonraki-kod?sinifId=${sinifId}`),

  olustur: (govde: UniteIstek) => api<UniteOzet>('/api/uniteler', { yontem: 'POST', govde }),

  guncelle: (id: string, govde: UniteIstek) =>
    api<UniteOzet>(`/api/uniteler/${id}`, { yontem: 'PUT', govde }),

  parametreler: (id: string, govde: ParametreIstek[]) =>
    api<BeyanParametresiDto[]>(`/api/uniteler/${id}/parametreler`, { yontem: 'PUT', govde }),

  sil: (id: string) => api<void>(`/api/uniteler/${id}`, { yontem: 'DELETE' }),
}
